import React, { useState, useEffect } from "react";
import { useParams, useHistory, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import bookingService, { Booking } from "../../services/bookingService";
import { ScrollNavbar } from "../../components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faExclamationTriangle,
  faArrowLeft,
  faCalendarDay,
  faClock,
  faCar,
  faRoute,
  faUser,
  faTag,
  faMoneyBillWave,
} from "@fortawesome/free-solid-svg-icons";

const BookingDetails = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const { user } = useAuth();
  const history = useHistory();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      history.push("/login");
      return;
    }

    const fetchBookingDetails = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await bookingService.getBookingById(bookingId);
        if (response.success && response.data) {
          setBooking(response.data);
        } else {
          setError(response.message || "Failed to fetch booking details.");
        }
      } catch (err: any) {
        setError(err.message || "An unexpected error occurred.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookingDetails();
  }, [bookingId, user, history]);

  const renderDetailItem = (
    icon: any,
    label: string,
    value?: string | number | null,
    isBold: boolean = false
  ) =>
    value ? (
      <div className="flex items-start py-3">
        <FontAwesomeIcon
          icon={icon}
          className="w-5 h-5 text-gray-400 mr-4 mt-1"
        />
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p
            className={`text-gray-800 ${isBold ? "font-semibold text-lg" : ""}`}
          >
            {value}
          </p>
        </div>
      </div>
    ) : null;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <FontAwesomeIcon
          icon={faSpinner}
          className="text-5xl text-blue-600 animate-spin"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-red-50">
        <div className="text-center">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="text-5xl text-red-500"
          />
          <p className="mt-4 text-xl text-red-700">Error: {error}</p>
          <Link
            to="/bookings"
            className="mt-6 inline-block bg-red-500 text-white font-medium py-2 px-4 rounded hover:bg-red-600"
          >
            Go Back
          </Link>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="text-center py-20">
        <p className="text-xl text-gray-600">Booking not found.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ScrollNavbar />
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => history.goBack()}
          className="flex items-center text-blue-600 hover:text-blue-800 font-medium mb-6"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back to My Bookings
        </button>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="p-6 bg-gray-800 text-white">
            <h1 className="text-2xl font-bold">
              Booking ID: {booking.booking_id.split("-")[0].toUpperCase()}
            </h1>
            <p className="text-gray-300 capitalize">{booking.booking_status}</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
              {renderDetailItem(
                faRoute,
                "Source",
                booking.SourceBooking?.name || booking.source,
                true
              )}
              {renderDetailItem(
                faRoute,
                "Destination",
                booking.DestinationBooking?.name || booking.destination,
                true
              )}
              {renderDetailItem(
                faCalendarDay,
                "Pickup Date",
                new Date(booking.pickup_date).toLocaleDateString("en-GB", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              )}
              {renderDetailItem(
                faClock,
                "Pickup Time",
                new Date(
                  `1970-01-01T${booking.pickup_time}`
                ).toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })
              )}
              {booking.return_date &&
                renderDetailItem(
                  faCalendarDay,
                  "Return Date",
                  new Date(booking.return_date).toLocaleDateString("en-GB", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                )}
              {booking.return_time &&
                renderDetailItem(
                  faClock,
                  "Return Time",
                  new Date(
                    `1970-01-01T${booking.return_time}`
                  ).toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })
                )}
              {renderDetailItem(faCar, "Cab Type", booking.cab_type)}
              {renderDetailItem(faUser, "Customer", user?.name)}
              {renderDetailItem(
                faMoneyBillWave,
                "Payment Mode",
                booking.payment_mode,
                true
              )}
              {renderDetailItem(
                faTag,
                "Total Fare",
                `₹${(booking.total_fare_bc || 0).toFixed(2)}`,
                true
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingDetails;
